'use no memo';
/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
/* eslint-disable check-react-ssr-usage/guard-browser-globals -- Pre-existing usages; this file is client-only via Loadable. */
/* SSR is also using the same elements to prevent duplication and risk for getting out of sync. Moved to fabric extension to
 prevent circular dependency with fabric-extension-handler and content-renderer */
import { css } from '@compiled/react';
import React, { memo, useCallback, useLayoutEffect, useState, useRef } from 'react';

import { UFOThirdPartySegment } from '@atlaskit/react-ufo/third-party-segment';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import { token } from '@atlaskit/tokens';

import { useExternalLinkHandlers } from '@confluence/adf-renderer/entry-points/useExternalLinkHandlers.ts';
import {
	delayIframeLoading,
	useDelayedIframe,
} from '@confluence/fabric-extension-lib/entry-points/renderer-extensions.ts';
import { ensureExternalLinksOpenInNewTab } from '@confluence/linking-platform/entry-points/ensureExternalLinksOpenInNewTab.ts';
import {
	LoadablePlaceholderClient,
	LoadablePlaceholderServer,
} from '@confluence/loadable/entry-points/LoadablePlaceholder.ts';
import type { Mode } from '@confluence/macro-tracker/entry-points/types.ts';
import { EDITOR, RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import type { FeatureFlagsType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { collectSSRedLegacyMacro, useSSRedLegacyMacroElement } from './SSRedLegacyMacro.ts';
import { NestedLcmADFRenderer } from './LegacyContentExtension/NestedLcmADFRenderer.tsx';

// Helper to find macro elements without Connect iframes, avoiding :has() which isn't supported in all browsers
const findMacroElementWithoutConnectIframe = (macroId: string): Element | null => {
	if (expValEquals('confluence-not-valid-selector', 'isEnabled', true)) {
		const elements = Array.from(
			document.querySelectorAll(`[data-fabric-macro="${macroId}"][data-skate-created]`),
		);
		for (const el of elements) {
			// Exclude elements that contain Connect iframe script or container
			if (
				!el.querySelector('.ap-iframe-body-script') &&
				!el.querySelector('.ap-iframe-container')
			) {
				return el;
			}
		}
		return null;
	} else {
		return document.querySelector(
			`[data-fabric-macro="${macroId}"][data-skate-created]:not(:has(.ap-iframe-body-script)):not(:has(.ap-iframe-container))`,
		);
	}
};

type LegacyMacroElementProps = {
	contentId: string;
	adf: ADNode;
	isInline: boolean;
	nodeProps: any;
	macroDataProps: any;
	mode: Mode;
	macroRepresentation: string;
	macroRenderedOutput: string | null;
	renderedByProvider: boolean;
	style?: any;
	children?: any;
	className?: string;
	featureFlags?: FeatureFlagsType;
	extensionHandlers?: ExtensionHandlers;
	mediaToken?: string;
	headingIdPrefix?: string;
	ssredElement?: Element;
	delayIframe: boolean;
	extensionKey?: string;
	height?: number;
};

const hasMacroBeenHydrated = {};

type InjectedMacroHtmlProps = {
	ElementTag: React.ElementType;
	className?: string;
	elementStyle: React.CSSProperties;
	macroHtml: string | null;
	nodeProps: LegacyMacroElementProps['nodeProps'];
	macroDataProps: LegacyMacroElementProps['macroDataProps'];
	delayIframe: boolean;
	extensionKey?: string;
	renderServerPlaceholder: boolean;
	macroId: string | undefined;
};

const InjectedMacroHtml = ({
	ElementTag,
	className,
	elementStyle,
	macroHtml,
	nodeProps,
	macroDataProps,
	delayIframe,
	extensionKey,
	renderServerPlaceholder,
	macroId,
}: InjectedMacroHtmlProps) => {
	const containerRef = useRef<HTMLElement | null>(null);
	useLayoutEffect(() => {
		ensureExternalLinksOpenInNewTab(containerRef.current);
	}, [macroHtml]);

	// Merge our ref with any parent ref from nodeProps so neither is clobbered.
	const setContainerRef = useCallback(
		(element: HTMLElement | null) => {
			containerRef.current = element;
			const parentRef = nodeProps?.ref;
			if (typeof parentRef === 'function') {
				parentRef(element);
			} else if (parentRef) {
				(parentRef as React.MutableRefObject<HTMLElement | null>).current = element;
			}
		},
		[nodeProps?.ref],
	);

	const { ref: _ignoredNodePropsRef, ...nodePropsWithoutRef } = nodeProps ?? {};

	return (
		<ElementTag
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={className}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={elementStyle}
			dangerouslySetInnerHTML={{ __html: macroHtml ?? '' }}
			{...nodePropsWithoutRef}
			{...macroDataProps}
			ref={setContainerRef}
			{...(delayIframe ? { 'data-delay-iframe': '1' } : {})}
			data-testid="legacy-macro-element"
			data-vc={`legacy-macro-element_${extensionKey || 'no-extension-key-provided'}`}
			data-vc-ignore-if-no-layout-shift
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': macroId }
				: { 'data-ssr-placeholder-replace': macroId })}
		/>
	);
};

function LegacyMacroDOMManipulatedByJQuery({
	macroId,
	content,
	containerRef,
}: {
	macroId: string;
	content: React.ReactNode;
	containerRef?: Function;
}) {
	const [hydrated, setHydrated] = useState(hasMacroBeenHydrated[macroId]);

	if (!hydrated) {
		// This needs to happen before rendering.
		// Collect the real DOM before replacing it with placeholder (LoadablePlaceholderClient)
		collectSSRedLegacyMacro(macroId);
	}

	const ref = useRef<HTMLInputElement | null>(null);
	const domElement = useSSRedLegacyMacroElement(macroId);

	useLayoutEffect(() => {
		if (hydrated) {
			const markerElement = ref.current;
			if (markerElement && domElement) {
				markerElement.insertAdjacentElement('afterend', domElement);

				// Set this callback is important because it signals setIsMacroResourcesLoaded in LegacyMacroRendererComponent
				// Which updates LegacyMacroRendererComponentResourcesSSR UFOLoadHold
				if (containerRef && typeof containerRef === 'function') {
					containerRef(domElement);
				}
				return () => {
					// We inserted domElement above which is outside the React lifecycle
					// When the actual macro is rendered, this component will unmount.
					// At that point, we need to remove the inserted element to avoid duplication
					try {
						domElement.parentNode?.removeChild(domElement);
					} catch {}
				};
			}
		} else {
			// External state instead of ref because the ancestor of this component
			// will change from FabricLegacyMacrosServerRenderer to PageContentRendererFabric
			// The external state is used to keep state between re-mounts.
			hasMacroBeenHydrated[macroId] = true;
			// Local state is used to trigger re-render
			setHydrated(true);
		}
	}, [hydrated, macroId, domElement, containerRef]);

	// If we didn't save the real DOM for whatever reason just render again.
	// We will get flicker but at least we will not break the page.
	if (!domElement) {
		return <>{content}</>;
	}

	/**
	 * There are certain SSRed macros that have jquery DOM manipulation.
	 * For those macros hydration is not going to work as React don't know about the DOM changes.
	 * If we put macroHtml on the page it will flip back to the initial state. eg page tree macro.
	 *
	 * - We display the SSR rendered result. This is when hydrated = false. This is to ensure React don't freak out in hydration.
	 * - However because LoadablePlaceholderClient converts DOM elements to React component on the fly. Click handlers are going to get lost.
	 * - We will later replace it with collected real DOM element. This is when hydrated = true.
	 * - Because the ancestor will change from FabricLegacyMacrosServerRenderer to PageContentRendererFabric all local state will be lost. hasMacroBeenHydrated will ensure we still use the real DOM with PageContentRendererFabric.
	 */
	return (
		<>
			{hydrated ? (
				// don't render this on the first paint to avoid breaking hydration
				<input type="hidden" ref={ref} data-legacy-macro-placeholder-ref />
			) : (
				<LoadablePlaceholderClient
					placeholderId={`legacy-macro-${macroId}`}
					// Fallback to content when no placeholder found
					clientPlaceholder={() => content}
					uniquePlaceholderId
				/>
			)}
		</>
	);
}

export const LegacyMacroElement: React.MemoExoticComponent<
	({
		contentId,
		className,
		isInline,
		nodeProps,
		macroDataProps,
		macroRepresentation,
		macroRenderedOutput,
		style,
		children,
		featureFlags,
		extensionHandlers,
		mediaToken,
		ssredElement,
		delayIframe,
		extensionKey,
	}: LegacyMacroElementProps) => React.JSX.Element | null
> = memo(
	({
		contentId,
		className,
		isInline,
		nodeProps,
		macroDataProps,
		macroRepresentation,
		macroRenderedOutput,
		style,
		children,
		featureFlags,
		extensionHandlers,
		mediaToken,
		headingIdPrefix,
		ssredElement,
		delayIframe,
		extensionKey,
	}: LegacyMacroElementProps): React.JSX.Element | null => {
		const ElementTag = isInline ? 'span' : 'div';
		const macroId = macroDataProps?.['data-fabric-macro'];
		const containerRef = nodeProps?.ref;
		const renderServerPlaceholder = useIsSSROrHydrating();

		const { eventHandlers: adfEventHandlers, onSetLinkTarget } = useExternalLinkHandlers();
		useDelayedIframe(delayIframe, ssredElement);

		if (macroRenderedOutput?.includes('transform-error') && isInline) {
			return null;
		}

		if (children) {
			return (
				<ElementTag
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={style}
					{...nodeProps}
					{...macroDataProps}
				>
					{children}
				</ElementTag>
			);
		}
		if (macroRepresentation === 'atlas_doc_format') {
			return macroRenderedOutput ? (
				<ElementTag
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={style}
					{...nodeProps}
					{...macroDataProps}
					data-testid="legacy-macro-adf"
					data-vc={`legacy-macro-element_${extensionKey || 'no-extension-key-provided'}`}
					data-vc-ignore-if-no-layout-shift
					{...(renderServerPlaceholder
						? { 'data-ssr-placeholder': macroId }
						: { 'data-ssr-placeholder-replace': macroId })}
				>
					<NestedLcmADFRenderer
						document={JSON.parse(macroRenderedOutput)}
						contentId={contentId}
						featureFlags={featureFlags ?? {}}
						extensionHandlers={extensionHandlers}
						mediaToken={mediaToken}
						appearance="full-width"
						eventHandlers={adfEventHandlers}
						onSetLinkTarget={onSetLinkTarget}
						headingIdPrefix={headingIdPrefix}
					/>
				</ElementTag>
			) : null;
		} else {
			const macroHtml = delayIframe ? delayIframeLoading(macroRenderedOutput) : macroRenderedOutput;

			const content = (
				<InjectedMacroHtml
					ElementTag={ElementTag}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					elementStyle={style}
					macroHtml={macroHtml}
					nodeProps={nodeProps}
					macroDataProps={macroDataProps}
					delayIframe={delayIframe}
					extensionKey={extensionKey}
					renderServerPlaceholder={renderServerPlaceholder}
					macroId={macroId}
				/>
			);

			if (
				window.__SSR_RENDERED__ &&
				// If the macro is server rendered
				macroHtml?.includes(`data-macro-id="${macroId}"`) &&
				// When hydrating, DOM contains element that was manipulated by jQuery
				// Exclude connect init script or connect iframe
				// There is no point of copy iframe because there is no way to move it without reloading the iframe content.
				(findMacroElementWithoutConnectIframe(macroId) ||
					// When we hydrated the macro, replacing the placeholder with the saved real DOM element
					document.querySelector('[data-legacy-macro-placeholder-ref]'))
			) {
				return (
					<LegacyMacroDOMManipulatedByJQuery
						macroId={macroId}
						content={content}
						containerRef={containerRef}
					/>
				);
			}

			return (
				<LoadablePlaceholderServer placeholderId={`legacy-macro-${macroId}`}>
					{content}
				</LoadablePlaceholderServer>
			);
		}
	},
);

const createClassnameForExtension = (adf: ADNode) => {
	const macroParams = adf?.attrs?.parameters?.macroParams || {};
	const extensionKey = adf?.attrs?.extensionKey || '';
	let key = `macro${extensionKey}`;
	if (extensionKey === 'jira') {
		if (!macroParams.key && !macroParams.count) {
			key += 'Table';
		} else if (adf?.type === 'inlineExtension') {
			key += 'SingleIssue';
		}
	}
	return key;
};

const LegacyMacroStyledElementBaseStyles = css({
	/** Fabric Editor sets "white-space: pre-wrap" which breaks layout for nested content not prepared for it, like macros */
	whiteSpace: 'normal',

	p: {
		whiteSpace: 'normal',
	},
	// Styled overrides for expand macro nested in adf-rendered macros to prevent horizontal scrollbar

	"[data-node-type='expand']": {
		marginLeft: token('space.0'),
		marginRight: token('space.0'),
	},
	// Fix code block rendering in legacy-rendered HTML.
	// The code macro uses class="code panel pdl" which inherits .panel styles
	// from panels.css, causing gray box artifacts.
	'.code.panel': {
		background: 'none !important',
		border: 'none !important',
	},
	'.code.panel .codeContent': {
		border: 'none !important',
	},
	'.code.panel td.code': {
		background: 'none !important',
	},
	clear: 'both',
	/**
	 * Jira Macros within page include are not displaying correctly when ADF macro renderer frontend flag is on.
	 * The jira macros need to be stretched from left to right. This manual css override will adjust it accordingly
	 * See ticket CERN-1743 for more info.
	 * */
	'table.confluenceTable': {
		tr: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'th.confluenceTh:nth-of-type(1), td.confluenceTd:nth-of-type(1)': {
				paddingLeft: token('space.150'),
			},
		},
	},
});

const LegacyMacroStyledElementJiraTableStyles = css({
	'.ak-renderer-document > div.ak-renderer-extension': {
		width: '100% !important',
	},
	display: 'block',
	maxWidth: '100%',
	overflowX: 'auto',
	th: {
		wordBreak: 'normal',
	},
});

const LegacyMacroStyledElementIncludeStyles = css({
	'.ak-renderer-document > div.ak-renderer-extension': {
		width: '100% !important',
	},
	/**
	 * Style override for nested page includes with recursion error message (ADF Macro Rendering/CERN-1805)
	 * */
	'.ak-renderer-document .ak-renderer-extension-overflow-container .error': {
		padding: token('space.075'),
	},

	'ak-renderer-document > .pm-table-container > .pm-table-wrapper': {
		padding: token('space.0'),
		margin: token('space.0'),
	},
	'.ak-renderer-document [data-testid="sticky-table-fixed"] > .pm-table-container': {
		marginBottom: token('space.0'),
	},
	'.ak-renderer-document [data-testid="sticky-table-fixed"] > .pm-table-container table': {
		marginTop: token('space.0'),
	},
});

const LegacyMacroStyledElementJiraSingleIssueStyles = css({
	'span.confluence-jim-macro.jira-issue': {
		overflowWrap: 'anywhere',
	},
});

const LegacyMacroStyledElementEditorModeStyles = css({
	position: 'relative',
	userSelect: 'none',
});

const LegacyMacroStyledElementRendererModeStyles = css({
	display: 'inline-block',
	padding: token('space.025'),
});

const LegacyMacroStyledElementInlineStyles = css({ clear: 'none' });

export const LegacyMacroStyledElement = ({
	adf,
	mode,
	isInline,
	...props
}: LegacyMacroElementProps): React.JSX.Element => {
	const hasMacroJiraTable = createClassnameForExtension(adf) === 'macrojiraTable';
	const hasMacroInclude = createClassnameForExtension(adf) === 'macroinclude';
	const hasMacroJiraSingleIssue = createClassnameForExtension(adf) === 'macrojiraSingleIssue';
	// Check if this contains Connect iframe content that should be wrapped in UFOThirdPartySegment
	const hasConnectIframe =
		props.macroRenderedOutput?.includes('ap-iframe-body-script') ||
		props.macroRenderedOutput?.includes('ap-iframe-container');

	const macroElement = (
		<LegacyMacroElement
			css={[
				LegacyMacroStyledElementBaseStyles,
				hasMacroJiraTable && LegacyMacroStyledElementJiraTableStyles,
				hasMacroInclude && LegacyMacroStyledElementIncludeStyles,
				hasMacroJiraSingleIssue && LegacyMacroStyledElementJiraSingleIssueStyles,
				mode === EDITOR && LegacyMacroStyledElementEditorModeStyles,
				isInline && LegacyMacroStyledElementInlineStyles,
				mode === RENDERER && isInline && LegacyMacroStyledElementRendererModeStyles,
			]}
			adf={adf}
			mode={mode}
			isInline={isInline}
			{...props}
		/>
	);

	// Wrap Connect iframe content in UFOThirdPartySegment to exclude from TTVC calculations
	return hasConnectIframe ? (
		<UFOThirdPartySegment name={`legacy-macro-${props.extensionKey || 'connect-iframe'}`}>
			{macroElement}
		</UFOThirdPartySegment>
	) : (
		macroElement
	);
};
